import type { JSX, MemoExoticComponent } from 'react';
import { useCallback, useMemo } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { AnnotationTypes, AnnotationMarkStates } from '@atlaskit/adf-schema/annotation';
import type { AnnotationManager } from '@atlaskit/editor-common/annotation';
import type {
	InlineCommentSelectionComponentProps,
	InlineCommentViewComponentProps,
	InlineCommentHoverComponentProps,
} from '@atlaskit/editor-common/types/annotation';
import type { AnnotationState } from '@atlaskit/editor-plugin-annotation/types';
import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import { useNativeEmbedCommentSupport } from '@atlaskit/native-embeds-common/use-comment-support';

import { getRendererInlineCommentOptions } from '@confluence/annotation-provider-inline-comments/entry-points/getRendererInlineCommentOptions.ts';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';

import {
	isExtensionCommentBlockNodeSupported,
	isRendererExtensionCommentBlockNodeSupported,
} from './extensionComments.ts';

export type RendererAnnotationProviderOptions = {
	pageId: string;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	triggerAnalyticsEvent: any;
	updateDocument: (adfDoc: JSONDocNode) => void;
	isArchived?: boolean;
};

export const useRendererAnnotationProviderOptions = ({
	pageId,
	validAnnotationsCallback,
	triggerAnalyticsEvent,
	updateDocument,
	isArchived,
}: RendererAnnotationProviderOptions): {
	inlineComment: {
		selectionComponent:
			| MemoExoticComponent<(props: InlineCommentSelectionComponentProps) => null>
			| undefined;
		viewComponent: MemoExoticComponent<
			(viewComponentProps: InlineCommentViewComponentProps) => JSX.Element
		>;
		hoverComponent: MemoExoticComponent<(props: InlineCommentHoverComponentProps) => null>;
		getState: (
			annotations: string[],
			isNestedRender: boolean,
		) => Promise<AnnotationState<AnnotationTypes, AnnotationMarkStates>[]>;
		updateSubscriber: any;
		disallowOnWhitespace: boolean;
		isToolbarAbove: boolean;
		allowDraftMode: boolean;
		allowCommentsOnMedia: boolean;
		isBlockNodeSupported: (node: ProseMirrorNode) => boolean;
		canCreateCommentOnBlockNode: (node: ProseMirrorNode) => boolean;
	};
	annotationManager: AnnotationManager | undefined;
} => {
	const supportsExtensionBlockComments = useNativeEmbedCommentSupport();
	const { setSelectionProps, setViewProps, setHoverProps } = useAnnotationsDispatch();
	const annotationManager = useAnnotationManager();

	const isBlockNodeSupported = useCallback(
		(node: ProseMirrorNode) =>
			isRendererExtensionCommentBlockNodeSupported(node, supportsExtensionBlockComments),
		[supportsExtensionBlockComments],
	);
	const canCreateCommentOnBlockNode = useCallback(
		(node: ProseMirrorNode) =>
			isExtensionCommentBlockNodeSupported(node, supportsExtensionBlockComments),
		[supportsExtensionBlockComments],
	);

	return useMemo(
		() => ({
			inlineComment: {
				...getRendererInlineCommentOptions({
					pageId,
					validAnnotationsCallback,
					triggerAnalyticsEvent,
					setSelectionProps,
					setHoverProps,
					setViewProps,
					updateDocument,
					isArchived,
				}),
				isBlockNodeSupported,
				canCreateCommentOnBlockNode,
			},
			annotationManager,
		}),
		[
			isBlockNodeSupported,
			canCreateCommentOnBlockNode,
			pageId,
			validAnnotationsCallback,
			triggerAnalyticsEvent,
			setSelectionProps,
			setHoverProps,
			setViewProps,
			updateDocument,
			isArchived,
			annotationManager,
		],
	);
};
